import { useEffect, useState, useRef, useCallback } from 'react';
import { Layout } from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import { 
  Image as ImageIcon, 
  HardDrive, 
  RefreshCw, 
  Trash2, 
  Upload, 
  Search,
  X,
  AlertCircle,
  CheckCircle2,
  Loader2
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatISTDate } from '../utils/timezone';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
};

export const ImageManager = () => {
  const { token } = useAuth();
  const [images, setImages] = useState([]);
  const [stats, setStats] = useState({ total_images: 0, total_size_bytes: 0, total_size_formatted: '0 B' });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedImage, setSelectedImage] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [compressionResult, setCompressionResult] = useState(null);
  const [replacing, setReplacing] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalImages, setTotalImages] = useState(0);
  const perPage = 50;

  const fetchData = useCallback(async (page = 1, search = '') => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString()
      });
      if (search.trim()) {
        params.append('search', search.trim());
      }
      
      const [imagesRes, statsRes] = await Promise.all([
        axios.get(`${API}/images?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/images/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      // Handle paginated response
      const imagesData = imagesRes.data;
      setImages(Array.isArray(imagesData.images) ? imagesData.images : []);
      setTotalImages(imagesData.total || 0);
      setTotalPages(imagesData.total_pages || 1);
      setCurrentPage(imagesData.page || 1);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Failed to fetch images');
      console.error(error);
      setImages([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData(currentPage, searchQuery);
  }, [fetchData, currentPage]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchData(1, searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery, fetchData]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleCompressAll = async () => {
    setCompressing(true);
    setCompressionResult(null);
    try {
      const response = await axios.post(`${API}/images/compress-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompressionResult(response.data);
      toast.success(`Compressed ${response.data.total_compressed} images, saved ${response.data.space_saved_formatted}`);
      fetchData(currentPage, searchQuery);
    } catch (error) {
      toast.error('Failed to compress images');
      console.error(error);
    } finally {
      setCompressing(false);
    }
  };

  const handleReplaceImage = async (file) => {
    if (!selectedImage || !file) return;
    
    setReplacing(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/images/${selectedImage.image_id}/replace`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`Image replaced! New size: ${response.data.new_size_formatted}`);
      setShowReplaceModal(false);
      setSelectedImage(null);
      fetchData(currentPage, searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to replace image');
      console.error(error);
    } finally {
      setReplacing(false);
    }
  };

  const handleDeleteImage = async () => {
    if (!selectedImage) return;
    
    setDeleting(true);
    try {
      await axios.delete(`${API}/images/${selectedImage.image_id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Image deleted successfully');
      setShowDeleteModal(false);
      setSelectedImage(null);
      fetchData(currentPage, searchQuery);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete image');
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  // Images are already filtered by the API when search is provided
  // No need for frontend filtering when using server-side search
  const displayedImages = images;

  const openReplaceModal = (image) => {
    setSelectedImage(image);
    setShowReplaceModal(true);
  };

  const openDeleteModal = (image) => {
    setSelectedImage(image);
    setShowDeleteModal(true);
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Image Manager
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Manage and optimize your media library
            </p>
          </div>
          <button
            onClick={handleCompressAll}
            disabled={compressing || images.length === 0}
            className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all active:scale-[0.98] text-sm sm:text-base disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {compressing ? (
              <>
                <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                Compressing...
              </>
            ) : (
              <>
                <RefreshCw className="w-5 h-5 mr-2" />
                Compress All Images
              </>
            )}
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-blue-100 rounded-lg">
                <ImageIcon className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Total Images</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_images}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-green-100 rounded-lg">
                <HardDrive className="w-5 h-5 sm:w-6 sm:h-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Total Storage</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.total_size_formatted}</p>
              </div>
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6 sm:col-span-2 lg:col-span-1">
            <div className="flex items-center">
              <div className="p-2 sm:p-3 bg-purple-100 rounded-lg">
                <RefreshCw className="w-5 h-5 sm:w-6 sm:h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-xs sm:text-sm font-medium text-slate-500">Avg. Image Size</p>
                <p className="text-xl sm:text-2xl font-bold text-slate-900">
                  {stats.total_images > 0 
                    ? formatSize(Math.round(stats.total_size_bytes / stats.total_images))
                    : '0 B'
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Compression Result */}
        {compressionResult && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Compression Complete</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Processed: {compressionResult.total_processed} images</p>
                  <p>Compressed: {compressionResult.total_compressed} images</p>
                  <p>Space saved: {compressionResult.space_saved_formatted}</p>
                </div>
                {compressionResult.errors.length > 0 && (
                  <div className="mt-2">
                    <p className="text-sm text-amber-700">Warnings: {compressionResult.errors.length} images had issues</p>
                  </div>
                )}
              </div>
              <button
                onClick={() => setCompressionResult(null)}
                className="ml-auto text-green-600 hover:text-green-800"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by filename or product..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 sm:py-3 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-base"
          />
        </div>

        {/* Images Grid */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 sm:h-12 sm:w-12 border-b-2 border-slate-900 mx-auto"></div>
            <p className="mt-4 text-sm sm:text-base text-slate-600">Loading images...</p>
          </div>
        ) : displayedImages.length === 0 ? (
          <div className="text-center py-12 bg-white border border-slate-200 rounded-lg">
            <ImageIcon className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-600">{searchQuery ? `No images found for "${searchQuery}"` : 'No images found'}</p>
            <p className="text-sm text-slate-500 mt-1">Upload images through the Products page</p>
          </div>
        ) : (
          <>
            {/* Results count */}
            <div className="text-sm text-slate-600">
              Showing {((currentPage - 1) * perPage) + 1} - {Math.min(currentPage * perPage, totalImages)} of {totalImages} images
              {searchQuery && ` matching "${searchQuery}"`}
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-3 sm:gap-4">
              {displayedImages.map((image) => (
                <div
                  key={image.image_id}
                  className="bg-white border border-slate-200 rounded-lg shadow-sm hover:shadow-md transition-shadow duration-200 overflow-hidden group"
                >
                  {/* Image Preview */}
                  <div className="aspect-square bg-slate-100 overflow-hidden relative">
                    <img
                      src={image.image_url}
                      alt={image.filename}
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                    {/* Size Badge */}
                    <div className={`absolute top-2 right-2 px-1.5 py-0.5 rounded text-xs font-medium ${
                      image.size_bytes > 50 * 1024 
                        ? 'bg-amber-100 text-amber-700' 
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {formatSize(image.size_bytes)}
                    </div>
                  </div>

                  {/* Image Info */}
                  <div className="p-2 sm:p-3">
                    <p className="text-xs sm:text-sm font-medium text-slate-900 truncate" title={image.filename}>
                      {image.filename}
                    </p>
                    <p className="text-xs text-slate-500 mt-0.5">
                      {image.width}×{image.height}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5 truncate" title={image.linked_products.join(', ')}>
                      Product: {image.linked_products.join(', ')}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      {formatISTDate(image.upload_date)}
                    </p>

                    {/* Actions */}
                    <div className="mt-2 flex gap-1.5">
                      <button
                        onClick={() => openReplaceModal(image)}
                        className="flex-1 inline-flex items-center justify-center px-2 py-1.5 bg-slate-100 text-slate-700 rounded text-xs font-medium hover:bg-slate-200 transition-colors"
                      >
                        <Upload className="w-3 h-3 mr-1" />
                        Replace
                      </button>
                      <button
                        onClick={() => openDeleteModal(image)}
                        className="px-2 py-1.5 bg-rose-50 text-rose-600 rounded hover:bg-rose-100 transition-colors"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 pt-4 border-t border-slate-200">
                <div className="text-sm text-slate-600">
                  Page {currentPage} of {totalPages}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-3 py-2 rounded-md border text-sm font-medium ${
                      currentPage === 1 
                        ? 'border-slate-200 text-slate-300 cursor-not-allowed' 
                        : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    ← Previous
                  </button>
                  
                  {/* Page Numbers */}
                  <div className="hidden sm:flex items-center gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, idx) => {
                      let pageNum;
                      if (totalPages <= 5) {
                        pageNum = idx + 1;
                      } else if (currentPage <= 3) {
                        pageNum = idx + 1;
                      } else if (currentPage >= totalPages - 2) {
                        pageNum = totalPages - 4 + idx;
                      } else {
                        pageNum = currentPage - 2 + idx;
                      }
                      
                      return (
                        <button
                          key={pageNum}
                          onClick={() => handlePageChange(pageNum)}
                          className={`w-10 h-10 rounded-md text-sm font-medium ${
                            currentPage === pageNum
                              ? 'bg-indigo-600 text-white'
                              : 'border border-slate-300 text-slate-700 hover:bg-slate-100'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-3 py-2 rounded-md border text-sm font-medium ${
                      currentPage === totalPages 
                        ? 'border-slate-200 text-slate-300 cursor-not-allowed' 
                        : 'border-slate-300 text-slate-700 hover:bg-slate-100'
                    }`}
                  >
                    Next →
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* Replace Modal */}
        {showReplaceModal && selectedImage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Replace Image</h3>
                  <button
                    onClick={() => { setShowReplaceModal(false); setSelectedImage(null); }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden mb-3">
                    <img
                      src={selectedImage.image_url}
                      alt={selectedImage.filename}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">Current:</span> {selectedImage.filename}
                  </p>
                  <p className="text-sm text-slate-500">
                    Size: {formatSize(selectedImage.size_bytes)} • {selectedImage.width}×{selectedImage.height}
                  </p>
                  <p className="text-sm text-slate-500">
                    Used in: {selectedImage.linked_products.join(', ')}
                  </p>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-700">
                    The new image will be automatically compressed and will replace this image everywhere it is used.
                  </p>
                </div>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleReplaceImage(e.target.files[0])}
                  className="hidden"
                />

                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowReplaceModal(false); setSelectedImage(null); }}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={replacing}
                    className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {replacing ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                        Uploading...
                      </>
                    ) : (
                      'Select New Image'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Delete Modal */}
        {showDeleteModal && selectedImage && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="p-4 sm:p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-slate-900">Delete Image</h3>
                  <button
                    onClick={() => { setShowDeleteModal(false); setSelectedImage(null); }}
                    className="text-slate-400 hover:text-slate-600"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="mb-4">
                  <div className="aspect-video bg-slate-100 rounded-lg overflow-hidden mb-3">
                    <img
                      src={selectedImage.image_url}
                      alt={selectedImage.filename}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <p className="text-sm text-slate-600">
                    <span className="font-medium">{selectedImage.filename}</span>
                  </p>
                  <p className="text-sm text-slate-500">
                    Used in: {selectedImage.linked_products.join(', ')}
                  </p>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                  <div className="flex items-start">
                    <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
                    <div className="ml-2">
                      <p className="text-sm text-amber-700 font-medium">Warning</p>
                      <p className="text-sm text-amber-600">
                        Primary product images cannot be deleted. Only additional images can be removed.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => { setShowDeleteModal(false); setSelectedImage(null); }}
                    className="flex-1 px-4 py-2 border border-slate-200 text-slate-700 rounded-md hover:bg-slate-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteImage}
                    disabled={deleting}
                    className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-colors disabled:opacity-50"
                  >
                    {deleting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 inline animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      'Delete Image'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
